// src/audioKit.ts — FINAL HARDENED v2 (adds tail guard preemption)
// Mono playback with preemption, flow-safe play (never deadlocks), cooldown,
// retries, cache-first preloading, background warming, LRU memory, in-flight
// de-duplication, optional transform (downmix/resample), telemetry (Amplitude-ready),
// and Sentry hooks. Tail guard: optional non-preempt window to keep chime tails smooth.

import { idle } from "./utils/idle";
import { withRetry } from "./utils/retry";
import { FetchError, DecodeError } from "./utils/errors";
import type {
    AudioId,
    AudioKit,
    AudioKitInit,
    BackgroundWarmOpts,
    PlayOpts,
    PreloadOpts,
    SoundItem,
    Logger,
    TelemetrySink,
    SentryLike,
    RetryPolicy,
    FlowGuardOpts,
    PlayResult,
    PlayResultStatus,
    PlayMode,
} from "./types";

export function defaultPickFormat(base: string): string {
    const a = document.createElement("audio");
    if (a.canPlayType('audio/ogg; codecs="opus"')) return `${base}.ogg`;
    if (a.canPlayType('audio/webm; codecs="opus"')) return `${base}.webm`;
    if (a.canPlayType('audio/mp4; codecs="mp4a.40.2"')) return `${base}.m4a`;
    return `${base}.mp3`;
}

const defaultLogger: Logger = {
    debug: (m, c) => console.debug("[audio-kit]", m, c ?? ""),
    info: (m, c) => console.info("[audio-kit]", m, c ?? ""),
    warn: (m, c) => console.warn("[audio-kit]", m, c ?? ""),
    error: (m, c) => console.error("[audio-kit]", m, c ?? ""),
};

const defaultRetry: RetryPolicy = {
    retries: 3,
    baseDelayMs: 300,
    maxDelayMs: 5000,
    jitter: true,
    timeoutMs: 12000,
};

export function createAudioKit(init: AudioKitInit = {}): AudioKit {
    const cacheName = init.cacheName ?? "audio-assets-v1";
    const pickFormat = init.pickFormat ?? defaultPickFormat;
    const logger = init.logger ?? defaultLogger;
    const telemetry = init.telemetry ?? ({ event: () => {} } as TelemetrySink);
    const sentry: SentryLike | undefined = init.sentry;
    const retry = init.retry ?? defaultRetry;
    const playMode: PlayMode = init.playMode ?? "mono";
    const cooldown = init.cooldown ?? {};
    const globalCooldown = cooldown.globalMinIntervalMs ?? 80;
    const perIdCooldown = cooldown.perIdMinIntervalMs ?? {};
    const mem = init.memory ?? {
        maxDecodedBytes: 32 * 1024 * 1024,
        maxBuffers: 256,
    };
    const transform = init.transform ?? {};
    const preempt = init.preempt ?? { minTailMs: 0, dropOnTail: true };

    // Check if we're in the browser environment
    if (typeof window === "undefined") {
        // Return a mock AudioKit for SSR
        return {
            resume: async () => {},
            isReady: () => false,
            preloadCritical: async () => {},
            warmBackground: () => () => {},
            play: () => {},
            playAndWait: async () => ({
                status: "error" as const,
                id: "",
                error: new Error("SSR"),
            }),
            has: () => false,
            getDuration: () => null,
            setMasterVolume: () => {},
            mute: () => {},
            unmute: () => {},
            stopAll: () => {},
            clear: () => {},
            getCurrentlyPlaying: () => null,
            pauseVoice: () => null,
            resumeVoice: () => false,
            clearCooldowns: () => {},
        };
    }

    // @ts-ignore (Safari prefix handled by union)
    const ctx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    const masterGain = ctx.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(ctx.destination);

    const store = new Map<AudioId, AudioBuffer>();
    const order: string[] = [];
    let decodedBytes = 0;
    const lastPlayedAt = new Map<string, number>();

    function sizeOf(buf: AudioBuffer) {
        return buf.length * buf.numberOfChannels * 4;
    }
    function touch(id: string) {
        const i = order.indexOf(id);
        if (i >= 0) order.splice(i, 1);
        order.push(id);
    }
    function evictIfNeeded() {
        while (
            (mem.maxDecodedBytes &&
                decodedBytes > (mem.maxDecodedBytes ?? Infinity)) ||
            (mem.maxBuffers && store.size > (mem.maxBuffers ?? Infinity))
        ) {
            const victim = order.shift();
            if (!victim) break;
            const b = store.get(victim);
            if (!b) continue;
            decodedBytes -= sizeOf(b);
            store.delete(victim);
            telemetry.event("audio.evict", { id: victim });
        }
    }
    function put(id: string, buf: AudioBuffer) {
        const sz = sizeOf(buf);
        if (store.has(id)) decodedBytes -= sizeOf(store.get(id)!);
        store.set(id, buf);
        decodedBytes += sz;
        touch(id);
        evictIfNeeded();
    }

    const inflight = new Map<string, Promise<void>>();

    async function cacheFirstBytes(url: string): Promise<ArrayBuffer> {
        return withRetry<ArrayBuffer>(
            async (_attempt, signal) => {
                if ("caches" in window) {
                    const cache = await caches.open(cacheName);
                    const req = new Request(url, { mode: "cors", signal });
                    const hit = await cache.match(req);
                    if (hit) {
                        telemetry.event("audio.cache_hit", { url });
                        return await hit.arrayBuffer();
                    }
                    const res = await fetch(req);
                    if (!res.ok)
                        throw new FetchError(
                            `HTTP ${res.status}`,
                            url,
                            res.status,
                        );
                    if (res.type === "basic" || res.type === "cors")
                        cache.put(req, res.clone()).catch(() => {});
                    return await res.arrayBuffer();
                } else {
                    const res = await fetch(url, { mode: "cors", signal });
                    if (!res.ok)
                        throw new FetchError(
                            `HTTP ${res.status}`,
                            url,
                            res.status,
                        );
                    return await res.arrayBuffer();
                }
            },
            retry,
            (attempt, err) => {
                if (err) {
                    logger.warn("fetch retry", { url, attempt, err });
                    telemetry.event("audio.fetch_retry", { url, attempt });
                }
            },
        ).catch((err) => {
            sentry?.captureException(err, {
                tags: { stage: "fetch" },
                extra: { url },
            });
            logger.error("fetch failed", { url, err });
            telemetry.event("audio.fetch_fail", { url });
            throw err;
        });
    }

    const decode = (id: string, bytes: ArrayBuffer) =>
        withRetry<AudioBuffer>(
            async () =>
                new Promise<AudioBuffer>((res, rej) => {
                    ctx.decodeAudioData(bytes.slice(0), res, () =>
                        rej(new DecodeError("decodeAudioData failed", id)),
                    );
                }),
            retry,
            (attempt, err) => {
                if (err) {
                    logger.warn("decode retry", { id, attempt, err });
                    telemetry.event("audio.decode_retry", { id, attempt });
                }
            },
        ).catch((err) => {
            sentry?.captureException(err, {
                tags: { stage: "decode" },
                extra: { id },
            });
            logger.error("decode failed", { id, err });
            telemetry.event("audio.decode_fail", { id });
            throw err;
        });

    async function transformBuffer(input: AudioBuffer) {
        const targetCh = transform.downmixToMono ? 1 : input.numberOfChannels;
        const targetSr = transform.targetSampleRate ?? input.sampleRate;
        if (
            targetCh === input.numberOfChannels &&
            targetSr === input.sampleRate
        )
            return input;
        const offline = new OfflineAudioContext(
            targetCh,
            Math.ceil(input.duration * targetSr),
            targetSr,
        );
        const src = offline.createBufferSource();
        src.buffer = input;
        src.connect(offline.destination);
        src.start();
        return await offline.startRendering();
    }

    function buildUrl(item: SoundItem): string {
        if (item.url) return item.url;
        if (!init.cdnBase || !item.base)
            throw new Error("Need url or (cdnBase+base)");
        const base = `${init.cdnBase.replace(/\/$/, "")}/${item.base}`;
        return pickFormat(base);
    }

    async function preloadOne(it: SoundItem) {
        // Check if already cached
        if (store.has(it.id)) return Promise.resolve();
        // Check if already being loaded
        if (inflight.has(it.id)) return inflight.get(it.id)!;
        const p = (async () => {
            const url = buildUrl(it);
            const bytes = await cacheFirstBytes(url);
            let buf = await decode(it.id, bytes);
            try {
                buf = await transformBuffer(buf);
            } catch {
                telemetry.event("audio.transform_fail", { id: it.id });
            }
            put(it.id, buf);
            telemetry.event("audio.preloaded", {
                id: it.id,
                bytes: bytes.byteLength,
                dur: buf.duration,
            });
        })().finally(() => inflight.delete(it.id));
        inflight.set(it.id, p);
        return p;
    }

    async function preloadCritical(list: SoundItem[], opts: PreloadOpts = {}) {
        console.log(`[SOUND_DEBUG] preloadCritical: `, list);
        const { onProgress, onError } = opts;
        const total = list.length;
        let done = 0;
        const concurrency = Math.max(1, opts.concurrency ?? 4);
        const queue = list.slice();
        const workers = Array.from({ length: concurrency }, async () => {
            while (queue.length) {
                const it = queue.shift()!;
                try {
                    await preloadOne(it);
                    onProgress?.(++done / total, it.id);
                } catch (e) {
                    onError?.(it.id, e);
                }
            }
        });
        await Promise.all(workers);
    }

    function warmBackground(list: SoundItem[], opts: BackgroundWarmOpts = {}) {
        console.log(`[SOUND_DEBUG] warmBackground: `, list);
        const { onProgress, onError, isActive } = opts;
        const total = list.length;
        let i = 0,
            loaded = 0,
            canceled = false;
        const step = async (deadline: any) => {
            if (canceled) return;
            if (isActive && !isActive()) {
                idle(step);
                return;
            }
            if (deadline.timeRemaining() < 4) {
                idle(step);
                return;
            }
            if (i >= list.length) return;
            const it = list[i++];
            try {
                await preloadOne(it);
                onProgress?.(++loaded / total, it.id);
            } catch (e) {
                onError?.(it.id, e);
            }
            idle(step);
        };
        idle(step);
        return () => {
            canceled = true;
        };
    }

    function isCoolingDown(id: string): boolean {
        const now = performance.now();
        const prev = lastPlayedAt.get(id) ?? 0;
        const minGap = perIdCooldown[id] ?? globalCooldown;
        return now - prev < minGap;
    }
    function markPlayedNow(id: string) {
        lastPlayedAt.set(id, performance.now());
    }

    // --- Single Voice Manager (mono) ---
    type VoiceCtrl = {
        id: string;
        src: AudioBufferSourceNode;
        gain: GainNode;
        timer: number | null;
        resolve: (res: PlayResult) => void;
        finalized: boolean;
        endAt: number;
        startAt: number; // When playback started (audio context time)
        buffer: AudioBuffer; // Keep reference to buffer for resume
        opts: PlayOpts; // Original play options
        pausedOffset?: number; // Position when paused (in seconds)
        isPaused?: boolean; // Track if currently paused
        priority: number; // Sound priority for preemption decisions
        triggerSource: string; // Source that triggered the sound
    };
    let voice: VoiceCtrl | null = null;

    // Store paused voice state for resume
    type PausedVoiceState = {
        id: string;
        buffer: AudioBuffer;
        opts: PlayOpts;
        pausedOffset: number;
        remainingDuration: number;
        resolve: (res: PlayResult) => void; // Preserve the callback for flow continuation
        priority: number; // Sound priority for preemption decisions
        triggerSource: string; // Source that triggered the sound
    };
    let pausedVoice: PausedVoiceState | null = null;

    function finalizeVoice(
        status: PlayResultStatus,
        id: string,
        error?: unknown,
    ) {
        if (!voice || voice.finalized) return;

        const finalizeTime = Date.now();
        const audioStartTime = voice.startAt ? voice.startAt * 1000 : 0;
        const actualDuration = audioStartTime
            ? finalizeTime - (audioStartTime - ctx.currentTime * 1000)
            : 0;
        const expectedDuration = voice.buffer
            ? voice.buffer.duration * 1000
            : 0;

        console.log(
            `[SOUND_DEBUG] finalizeVoice called for ${id}: status=${status}, actualDuration=${actualDuration.toFixed(0)}ms, expectedDuration=${expectedDuration.toFixed(0)}ms`,
        );

        try {
            if (voice.timer) {
                clearTimeout(voice.timer);
                console.log(`[SOUND_DEBUG] Cleared timeout for ${id}`);
            }
        } catch {}
        try {
            voice.src.onended = null;
        } catch {}
        try {
            voice.src.disconnect();
            voice.gain.disconnect();
        } catch {}
        voice.finalized = true;
        voice.resolve({ status, id, error });
        voice = null;

        console.log(
            `[SOUND_DEBUG] finalizeVoice completed for ${id} at ${new Date().toISOString()}`,
        );
    }

    function shouldDropForTail(): boolean {
        if (!voice || !preempt.minTailMs) return false;
        const remainingMs = Math.max(0, (voice.endAt - ctx.currentTime) * 1000);
        return remainingMs <= (preempt.minTailMs || 0);
    }

    function preemptActive(
        newId?: string,
        priority: number = 0,
        triggerSource: string = "unknown",
        reason?: string,
    ) {
        const callStack = new Error().stack;
        const caller = callStack?.split("\n")[2]?.trim() || "unknown";

        console.log(
            `[SOUND_DEBUG] === PREEMPTION START === Reason: ${reason || "UNKNOWN"} at ${new Date().toISOString()}`,
        );
        console.log(`[SOUND_DEBUG] Preemption called from: ${caller}`);
        console.log(
            `[AudioKit] New sound request: ${newId || "unknown"}, priority: ${priority}, source: ${triggerSource}`,
        );

        // Check priority-based preemption for active voice
        if (voice) {
            console.log(
                `[AudioKit] Current playing: ${voice.id}, priority: ${voice.priority}, source: ${voice.triggerSource}`,
            );

            // Check if new sound has lower priority than current
            if (priority < voice.priority) {
                console.log(
                    `[AudioKit] Preemption decision: BLOCKED (new: ${priority} vs current: ${voice.priority})`,
                );
                console.log(
                    `[AudioKit] Priority block: ${newId} (${priority}) cannot preempt ${voice.id} (${voice.priority})`,
                );
                return false; // Don't preempt
            }

            console.log(
                `[AudioKit] Preemption decision: ALLOWED (new: ${priority} vs current: ${voice.priority})`,
            );
        }

        // If there's a paused voice waiting, check its priority too
        if (pausedVoice && pausedVoice.resolve) {
            if (priority < pausedVoice.priority) {
                console.log(
                    `[AudioKit] Priority block: ${newId} (${priority}) cannot preempt paused ${pausedVoice.id} (${pausedVoice.priority})`,
                );
                return false; // Don't preempt paused voice
            }

            console.log(
                `[SOUND_DEBUG] PREEMPTING PAUSED audio: ${pausedVoice.id} - New audio: ${newId || "unknown"} - Reason: ${reason}`,
            );
            pausedVoice.resolve({ status: "preempted", id: pausedVoice.id });
            pausedVoice = null;
        }

        if (!voice) {
            console.log(`[SOUND_DEBUG] No active voice to preempt`);
            return true; // Can play
        }

        const prev = voice;
        const timePlayed = prev.startAt
            ? (ctx.currentTime - prev.startAt) * 1000
            : 0;
        const expectedDuration = prev.buffer ? prev.buffer.duration * 1000 : 0;

        console.log(
            `[SOUND_DEBUG] PREEMPTING ACTIVE audio: ${prev.id} - Being preempted by${newId ? ` "${newId}"` : " new audio"}`,
        );
        console.log(
            `[SOUND_DEBUG] Audio ${prev.id} played for ${timePlayed.toFixed(0)}ms of expected ${expectedDuration.toFixed(0)}ms`,
        );

        finalizeVoice("preempted", prev.id);
        try {
            prev.src.stop(0);
        } catch {}

        console.log(`[SOUND_DEBUG] === PREEMPTION END === ${prev.id}`);
        return true; // Successfully preempted
    }

    function startVoice(
        id: string,
        buf: AudioBuffer,
        opts: PlayOpts,
        guardMs: number,
        resolve: (r: PlayResult) => void,
    ) {
        const startVoiceTime = Date.now();
        console.log(
            `[SOUND_DEBUG] startVoice BEGIN for ${id} at ${new Date().toISOString()}`,
        );

        const src = ctx.createBufferSource();
        const gain = ctx.createGain();
        src.buffer = buf;
        if (opts.rate !== undefined) src.playbackRate.value = opts.rate;
        if (opts.detune !== undefined && "detune" in src)
            (src as any).detune.value = opts.detune;
        gain.gain.value = opts.volume ?? 1;
        src.connect(gain).connect(masterGain);

        const playRate = opts.rate ?? 1;
        const durSec = buf.duration / playRate;
        const startAt = (opts.when ?? 0) + ctx.currentTime;
        const endAt = startAt + durSec;

        console.log(
            `[SOUND_DEBUG] Audio params for ${id}: bufferDuration=${buf.duration}s, playRate=${playRate}, adjustedDuration=${durSec}s, guardMs=${guardMs}`,
        );

        const ctrl: VoiceCtrl = {
            id,
            src,
            gain,
            timer: null,
            resolve,
            finalized: false,
            endAt,
            startAt,
            buffer: buf,
            opts,
            isPaused: false,
            priority: opts.priority ?? 0, // Default priority 0
            triggerSource: opts.triggerSource ?? "unknown", // Default source
        };
        src.onended = () => {
            const endedTime = Date.now();
            const actualDuration = endedTime - startVoiceTime;
            console.log(
                `[SOUND_DEBUG] Audio ONENDED fired for ${id} after ${actualDuration}ms (expected ${durSec * 1000}ms)`,
            );
            finalizeVoice("ok", id);
        };
        voice = ctrl;
        ctrl.timer = window.setTimeout(() => {
            const timeoutTime = Date.now();
            const elapsedTime = timeoutTime - startVoiceTime;
            console.log(
                `[SOUND_DEBUG] GUARD TIMEOUT fired for ${id} after ${elapsedTime}ms (guard was set to ${guardMs}ms)`,
            );
            finalizeVoice("timeout", id);
        }, guardMs);

        console.log(
            `[SOUND_DEBUG] Starting audio playback for ${id}: duration=${durSec.toFixed(2)}s, guard=${guardMs}ms, startAt=${startAt}`,
        );

        src.start(startAt);

        console.log(
            `[SOUND_DEBUG] Audio STARTED for ${id} at ${Date.now()}ms (${Date.now() - startVoiceTime}ms after startVoice)`,
        );
    }

    function pauseVoice(): PausedVoiceState | null {
        // If already paused, return existing state
        if (pausedVoice && !voice) {
            console.log(`[SOUND_DEBUG] Already paused: ${pausedVoice.id}`);
            return pausedVoice;
        }

        if (!voice || voice.finalized) return null;

        // Calculate how far we are into the audio
        const elapsed = ctx.currentTime - voice.startAt;
        const playbackRate = voice.opts.rate ?? 1;
        const actualElapsed = elapsed * playbackRate;

        // Don't pause if already finished
        if (actualElapsed >= voice.buffer.duration) return null;

        console.log(
            `[SOUND_DEBUG] Pausing audio: ${voice.id} at ${actualElapsed.toFixed(2)}s`,
        );

        // Store pause state with callback preserved
        pausedVoice = {
            id: voice.id,
            buffer: voice.buffer,
            opts: voice.opts,
            pausedOffset: Math.max(0, actualElapsed),
            remainingDuration: voice.buffer.duration - actualElapsed,
            resolve: voice.resolve, // Preserve the callback for flow continuation
            priority: voice.priority, // Preserve priority
            triggerSource: voice.triggerSource, // Preserve trigger source
        };

        // Stop current playback

        // Clear onended handler BEFORE stopping to prevent false "ended naturally"
        try {
            voice.src.onended = null;
        } catch {}

        try {
            voice.src.stop(0);
        } catch {}

        // Clean up but don't finalize with preempted status
        if (voice.timer) clearTimeout(voice.timer);
        voice = null;

        return pausedVoice;
    }

    function resumeVoice(): boolean {
        if (!pausedVoice) {
            console.log(
                `[SOUND_DEBUG] Cannot resume: no paused voice (pausedVoice is null)`,
            );
            return false;
        }

        if (voice) {
            console.log(
                `[SOUND_DEBUG] Cannot resume ${pausedVoice.id}: another voice (${voice.id}) is playing`,
            );
            return false;
        }

        const {
            id,
            buffer,
            opts,
            pausedOffset,
            remainingDuration,
            resolve,
            priority,
            triggerSource,
        } = pausedVoice;
        console.log(
            `[SOUND_DEBUG] pausedVoice exists - id: ${id}, offset: ${pausedOffset.toFixed(2)}s, remaining: ${remainingDuration.toFixed(2)}s`,
        );

        // Ensure audio context is running
        if (ctx.state === "suspended") {
            console.log(
                `[SOUND_DEBUG] Audio context suspended, cannot resume ${id}`,
            );
            return false;
        }

        console.log(
            `[SOUND_DEBUG] Resuming audio: ${id} from ${pausedOffset.toFixed(2)}s`,
        );

        // Create new source starting from pausedOffset
        const src = ctx.createBufferSource();
        const gain = ctx.createGain();

        src.buffer = buffer;
        if (opts.rate !== undefined) src.playbackRate.value = opts.rate;
        if (opts.detune !== undefined && "detune" in src)
            (src as any).detune.value = opts.detune;
        gain.gain.value = opts.volume ?? 1;

        src.connect(gain).connect(masterGain);

        // Calculate timing
        const playRate = opts.rate ?? 1;
        const adjustedRemainingDuration = remainingDuration / playRate;
        const now = ctx.currentTime;
        const virtualStartAt = now - pausedOffset / playRate; // Adjust start time as if it started earlier
        const endAt = now + adjustedRemainingDuration;

        // Create voice control with restored callback
        const ctrl: VoiceCtrl = {
            id,
            src,
            gain,
            buffer,
            opts,
            timer: null,
            resolve: resolve, // Restore the original callback for flow continuation
            finalized: false,
            startAt: virtualStartAt,
            endAt,
            isPaused: false,
            pausedOffset,
            priority, // Restore priority
            triggerSource, // Restore trigger source
        };

        src.onended = () => {
            console.log(`[SOUND_DEBUG] Resumed audio ended naturally: ${id}`);
            finalizeVoice("ok", id);
        };

        voice = ctrl;

        // Set timer for guard
        const guardMs = adjustedRemainingDuration * 1000 + 1500;
        ctrl.timer = window.setTimeout(() => {
            console.log(`[SOUND_DEBUG] Resumed audio timeout: ${id}`);
            finalizeVoice("timeout", id);
        }, guardMs);

        // Start from the paused position
        // start(when, offset, duration)
        src.start(0, pausedOffset);

        // Clear paused state after successful resume
        console.log(
            `[SOUND_DEBUG] Clearing pausedVoice after successful resume of ${id}`,
        );
        pausedVoice = null;

        return true;
    }

    function clearCooldowns() {
        lastPlayedAt.clear();
        console.log(`[SOUND_DEBUG] Cooldowns cleared`);
    }

    async function resume() {
        if (ctx.state === "suspended") await ctx.resume();
    }
    function isReady() {
        return ctx.state === "running";
    }

    function play(id: AudioId, opts: PlayOpts = {}) {
        // If there's a paused voice that won't be resumed, clear it
        if (pausedVoice && pausedVoice.id !== id) {
            console.log(
                `[SOUND_DEBUG] Clearing paused audio ${pausedVoice.id} - New audio ${id} requested`,
            );
            if (pausedVoice.resolve) {
                pausedVoice.resolve({
                    status: "preempted",
                    id: pausedVoice.id,
                });
            }
            pausedVoice = null;
        }

        const buf = store.get(id);
        if (!buf) {
            console.log(
                `[SOUND_DEBUG] Audio play failed: ${id} - Not in store`,
            );
            return;
        }
        if (isCoolingDown(id)) {
            console.log(`[SOUND_DEBUG] Audio dropped: ${id} - Cooldown active`);
            cooldown.onDrop?.(id);
            telemetry.event("audio.drop_cooldown", { id });
            return;
        }
        if (playMode === "mono") {
            if (shouldDropForTail() && (preempt.dropOnTail ?? true)) {
                console.log(
                    `[SOUND_DEBUG] Audio dropped: ${id} - Protecting tail of current audio`,
                );
                telemetry.event("audio.drop_tail", { id });
                return; // drop new play; keep current tail
            }
            console.log(
                `[SOUND_DEBUG] New audio request: ${id} - Will preempt current audio (FIRE_MODE)`,
            );
            preemptActive(
                id,
                opts.priority ?? 0,
                opts.triggerSource ?? "unknown",
                "PREEMPT_FIRE_MODE",
            );
        }
        const guard = Math.max(
            3000,
            (buf.duration / (opts.rate ?? 1)) * 1000 + 1500,
        );
        startVoice(id, buf, opts, guard, () => {});
        markPlayedNow(id);
        telemetry.event("audio.play_ok", { id, mode: "fire" });
    }

    // CRITICAL: This function returns a promise that MUST ALWAYS resolve
    // Multiple safety mechanisms ensure the flow never gets stuck:
    // 1. Normal completion: Audio finishes naturally
    // 2. Preemption: New audio interrupts, old promise resolves
    // 3. Timeout: Guard timer ensures long audio doesn't hang
    // 4. Pause/Resume: Callbacks preserved across pause/resume
    // 5. Pause without resume: Promise resolved when cleared/stopped
    // 6. Errors: All errors caught and promise resolved
    // 7. Ultimate safety: Force resolution after guard + 5 seconds
    async function playAndWait(
        id: AudioId,
        opts: PlayOpts & FlowGuardOpts = {},
    ): Promise<PlayResult> {
        const playAndWaitStartTime = Date.now();
        console.log(
            `[SOUND_DEBUG] playAndWait START for ${id} at ${new Date().toISOString()}`,
        );
        // If there's a paused voice that won't be resumed, resolve it first
        if (pausedVoice && pausedVoice.id !== id) {
            const callStack = new Error().stack;
            const caller = callStack?.split("\n")[2]?.trim() || "unknown";

            console.log(
                `[SOUND_DEBUG] === DIRECT PREEMPTION in playAndWait === at ${new Date().toISOString()}`,
            );
            console.log(
                `[SOUND_DEBUG] Clearing paused audio ${pausedVoice.id} - New audio ${id} requested`,
            );
            console.log(`[SOUND_DEBUG] Called from: ${caller}`);

            if (pausedVoice.resolve) {
                console.log(
                    `[SOUND_DEBUG] Resolving paused voice ${pausedVoice.id} with preempted status (PREEMPT_DIFFERENT_AUDIO)`,
                );
                pausedVoice.resolve({
                    status: "preempted",
                    id: pausedVoice.id,
                });
            }
            pausedVoice = null;
        }

        const buf = store.get(id);
        if (!buf) {
            console.log(
                `[SOUND_DEBUG] Audio play failed: ${id} - Not in store (took ${Date.now() - playAndWaitStartTime}ms)`,
            );
            telemetry.event("audio.play_missing", { id });
            return { status: "missing", id };
        }

        console.log(
            `[SOUND_DEBUG] Buffer found for ${id}: duration=${buf.duration}s, sampleRate=${buf.sampleRate}`,
        );
        if (isCoolingDown(id)) {
            console.log(`[SOUND_DEBUG] Audio dropped: ${id} - Cooldown active`);
            cooldown.onDrop?.(id);
            telemetry.event("audio.drop_cooldown", { id });
            return { status: "dropped", id };
        }
        if (ctx.state !== "running") {
            try {
                await ctx.resume();
            } catch (e) {
                telemetry.event("audio.play_error", { id });
                return { status: "error", id, error: e };
            }
        }
        if (playMode === "mono") {
            if (shouldDropForTail() && (preempt.dropOnTail ?? true)) {
                console.log(
                    `[SOUND_DEBUG] Audio dropped: ${id} - Protecting tail of current audio`,
                );
                telemetry.event("audio.drop_tail", { id });
                return { status: "dropped", id };
            }
            console.log(
                `[SOUND_DEBUG] New audio request: ${id} - Will preempt current audio (MONO_MODE)`,
            );
            const callStack = new Error().stack;
            console.log(
                `[SOUND_DEBUG] PlayAndWait preemption triggered from: ${callStack?.split("\n")[2]?.trim()}`,
            );
            const canPreempt = preemptActive(
                id,
                opts.priority ?? 0,
                opts.triggerSource ?? "unknown",
                "PREEMPT_MONO_MODE",
            );
            if (!canPreempt) {
                // Priority blocked - return without playing
                console.log(
                    `[AudioKit] Sound ${id} blocked by priority system`,
                );
                return { status: "dropped" as PlayResultStatus, id };
            }
        }

        const expectedMs = (buf.duration / (opts.rate ?? 1)) * 1000;
        const guard = Math.max(0, opts.maxMs ?? expectedMs + 1500);

        console.log(
            `[SOUND_DEBUG] Timeout settings for ${id}: expectedMs=${expectedMs.toFixed(0)}, guardMs=${guard.toFixed(0)}, safetyMs=${guard + 5000}`,
        );

        markPlayedNow(id);

        return new Promise<PlayResult>((resolve) => {
            let resolved = false;
            const promiseCreatedTime = Date.now();

            console.log(
                `[SOUND_DEBUG] Promise created for ${id} at ${promiseCreatedTime}ms (${promiseCreatedTime - playAndWaitStartTime}ms since playAndWait start)`,
            );

            // Ultimate safety timeout - ensure promise ALWAYS resolves
            const safetyTimeoutMs = guard + 5000;
            const safetyTimeout = window.setTimeout(() => {
                if (!resolved) {
                    const timeElapsed = Date.now() - promiseCreatedTime;
                    console.log(
                        `[SOUND_DEBUG] SAFETY TIMEOUT TRIGGERED: Force resolving ${id} after ${timeElapsed}ms (safety was set to ${safetyTimeoutMs}ms)`,
                    );
                    resolved = true;
                    resolve({ status: "timeout", id });
                }
            }, safetyTimeoutMs); // Give extra 5 seconds beyond normal guard

            // Wrap the original resolve to clear safety timeout
            const wrappedResolve = (res: PlayResult) => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(safetyTimeout);
                    const resolveTime = Date.now();
                    const promiseDuration = resolveTime - promiseCreatedTime;
                    const totalDuration = resolveTime - playAndWaitStartTime;

                    console.log(
                        `[SOUND_DEBUG] playAndWait RESOLVED for ${id}: status=${res.status}, promiseDuration=${promiseDuration}ms, totalDuration=${totalDuration}ms`,
                    );

                    telemetry.event(`audio.play_${res.status}`, { id });
                    if (res.status !== "ok") {
                        console.log(
                            `[SOUND_DEBUG] Audio play result: ${id} - ${res.status}`,
                        );
                    }
                    resolve(res);
                }
            };

            try {
                startVoice(id, buf, opts, guard, wrappedResolve);
            } catch (error) {
                console.log(`[SOUND_DEBUG] Error starting voice: ${error}`);
                wrappedResolve({ status: "error", id, error });
            }
        });
    }

    function setMasterVolume(v: number) {
        masterGain.gain.value = Math.max(0, Math.min(1, v));
    }
    function mute() {
        setMasterVolume(0);
    }
    function unmute() {
        setMasterVolume(1);
    }
    function has(id: AudioId) {
        return store.has(id);
    }
    function getDuration(id: AudioId): number | null {
        const buf = store.get(id);
        return buf ? buf.duration : null;
    }
    function getCurrentlyPlaying() {
        return voice?.id ?? null;
    }
    function stopAll() {
        const callStack = new Error().stack;
        const caller = callStack?.split("\n")[2]?.trim() || "unknown";

        console.log(
            `[SOUND_DEBUG] === STOP ALL CALLED === at ${new Date().toISOString()}`,
        );
        console.log(`[SOUND_DEBUG] stopAll called from: ${caller}`);

        if (voice) {
            console.log(
                `[SOUND_DEBUG] Stopping all audio - Current: ${voice.id}`,
            );
            preemptActive("stopAll", 1000, "system", "PREEMPT_STOP_ALL");
        }
        // If there's a paused voice with pending promise, resolve it
        if (pausedVoice && pausedVoice.resolve) {
            console.log(
                `[SOUND_DEBUG] Resolving paused voice in stopAll() - ${pausedVoice.id} (PREEMPT_STOP_ALL_PAUSED)`,
            );
            // Resolve the paused promise so flow continues
            pausedVoice.resolve({ status: "preempted", id: pausedVoice.id });
            pausedVoice = null;
        } else if (pausedVoice) {
            console.log(
                `[SOUND_DEBUG] Clearing pausedVoice in stopAll() - was ${pausedVoice.id}`,
            );
            pausedVoice = null;
        }
    }
    function clear(keepPlaying = true) {
        // Default to keeping audio playing
        const callStack = new Error().stack;
        const caller = callStack?.split("\n")[2]?.trim() || "unknown";

        console.log(
            `[SOUND_DEBUG] === CLEAR CALLED === keepPlaying=${keepPlaying} at ${new Date().toISOString()}`,
        );
        console.log(`[SOUND_DEBUG] clear called from: ${caller}`);

        if (!keepPlaying) {
            stopAll(); // This will handle paused voice resolution
        } else if (pausedVoice && pausedVoice.resolve) {
            // If keeping current playback but have a paused voice, resolve it
            console.log(
                `[SOUND_DEBUG] Resolving paused voice in clear() - ${pausedVoice.id} (PREEMPT_CLEAR)`,
            );
            pausedVoice.resolve({ status: "preempted", id: pausedVoice.id });
            pausedVoice = null;
        }

        store.clear();
        decodedBytes = 0;
        order.splice(0);
    }

    document.addEventListener("visibilitychange", () => {
        if (
            document.visibilityState === "visible" &&
            ctx.state === "suspended"
        ) {
            ctx.resume().catch(() => {});
        }
    });

    return {
        resume,
        isReady,
        preloadCritical,
        warmBackground,
        play,
        playAndWait,
        has,
        getDuration,
        setMasterVolume,
        mute,
        unmute,
        stopAll,
        clear,
        getCurrentlyPlaying,
        pauseVoice,
        resumeVoice,
        clearCooldowns,
    };
}
